import React, { useEffect, useRef, useState } from 'react';
import { Grid } from '@mui/material';
import { scrollingBannerStyles as classes } from './styleObjects/ScrollingBanner';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Keyboard, Virtual, A11y } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/virtual';
import type { Swiper as SwiperType } from 'swiper';
import { ScrollingBannerSlide } from './ScrollingBannerSlide';
import { ScrollingBannerContent } from '@models/section/scrollingBannerSection';
import { SectionAnimation } from '@models/section';

type ScrollingBannerProps = ScrollingBannerContent & {
    animations: SectionAnimation;
    zIndex: number;
    lazyLoad: boolean;
};

export function ScrollingBanner({
    banners,
    settings,
    animations,
    zIndex,
    lazyLoad,
}: ScrollingBannerProps): React.ReactElement {
    const reducedMotion = useRef(false);
    const sliderRef = useRef<HTMLDivElement>(null);
    const swiperRef = useRef<SwiperType | null>(null);
    const [activeSlideIndex, setActiveSlideIndex] = useState(0);

    function handleSlideChange(swiper: SwiperType): void {
        setActiveSlideIndex(swiper.activeIndex % banners.length);
    }

    useEffect(() => {
        if (sliderRef.current) {
            const activeSlide = sliderRef.current.querySelector('.swiper-slide-active');
            if (activeSlide) {
                const activeSlideLink = activeSlide.querySelector('a');
                const activeSlideButton = activeSlide.querySelector('button');
                const activeSlideInteractiveElement = activeSlideLink || activeSlideButton;
                activeSlideInteractiveElement && activeSlideInteractiveElement.setAttribute('tabindex', '0');
                return () => {
                    activeSlideInteractiveElement && activeSlideInteractiveElement.setAttribute('tabindex', '-1');
                };
            }
        }
    }, [activeSlideIndex]);

    useEffect(() => {
        reducedMotion.current = window.matchMedia && window.matchMedia('(prefers-reduced-motion)').matches;
    }, []);

    return (
        <Grid
            container
            css={[classes.scroll, { padding: settings.spacing, zIndex: zIndex }]}
            className={`${animations.animationsEnabled && 'animateContainers'}`}
            ref={sliderRef}
        >
            <Swiper
                modules={[Virtual, Autoplay, Keyboard, A11y]}
                onBeforeInit={(swiper) => {
                    swiperRef.current = swiper;
                }}
                onSlideChange={handleSlideChange}
                virtual={!settings.isInfinite}
                autoplay={
                    settings.isAutoplay
                        ? {
                            delay: settings.autoplayDelay || 3000,
                            disableOnInteraction: false,
                        }
                        : false
                }
                keyboard={{
                    enabled: true,
                }}
                a11y={{}}
                speed={2000}
                cssMode={reducedMotion.current || !settings.isAnimated}
                loop={settings.isInfinite}
                slidesPerView={1}
            >
                {banners.map((banner, index) => (
                    <SwiperSlide key={index} virtualIndex={index}>
                        <ScrollingBannerSlide lazyLoad={lazyLoad} banner={banner} />
                    </SwiperSlide>
                ))}
            </Swiper>
        </Grid>
    );
}

ScrollingBanner.defaultProps = {
    settings: {},
    animations: {
        animationsEnabled: false,
    },
};
